import React, { useState, useEffect } from 'react';
import { Search, X, Check, Globe } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const POPULAR_TLDS = [
  'com', 'net', 'org', 'io', 'co', 'ai', 'app', 'dev', 'xyz', 'info',
  'me', 'us', 'uk', 'ca', 'de', 'fr', 'es', 'nl', 'it', 'ch',
  'se', 'no', 'fi', 'pl', 'be', 'au', 'in', 'jp', 'cn', 'br',
  'za', 'biz', 'online', 'site', 'tech', 'store', 'cloud', 'digital', 'solutions', 'agency'
];

interface EnhancedSearchProps {
  autoFocus?: boolean;
  initialValue?: string;
  initialSearchType?: 'keyword' | 'domain';
  initialTlds?: string[];
  onSubmit?: (input: string, searchType: 'keyword' | 'domain', selectedTlds?: string[]) => void;
}

const EnhancedSearch: React.FC<EnhancedSearchProps> = ({
  autoFocus = false,
  initialValue = '',
  initialSearchType = 'keyword',
  initialTlds,
  onSubmit,
}) => {
  const navigate = useNavigate();
  const [searchType, setSearchType] = useState<'keyword' | 'domain'>(initialSearchType);
  const [input, setInput] = useState(initialValue);
  const [selectedTlds, setSelectedTlds] = useState<string[]>(initialTlds ?? POPULAR_TLDS);
  const [error, setError] = useState<string | null>(null);
  const [showTldOptions, setShowTldOptions] = useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 150);
    }
  }, [autoFocus]);

  useEffect(() => {
    const handleFocusEvent = () => {
      if (inputRef.current) setTimeout(() => inputRef.current?.focus(), 150);
    };
    window.addEventListener('focus-search-input', handleFocusEvent);
    return () => window.removeEventListener('focus-search-input', handleFocusEvent);
  }, []);

  const validateDomain = (d: string) =>
    /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/.test(d) &&
    d.length <= 253 && d.includes('.');

  const validateKeyword = (k: string) =>
    /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?$/.test(k) &&
    k.length > 0 && k.length <= 63 && !k.includes('.');

  const handleSearchTypeChange = (type: 'keyword' | 'domain') => {
    setSearchType(type);
    setInput('');
    setError(null);
    if (type === 'keyword') setSelectedTlds(POPULAR_TLDS);
  };

  const toggleTld = (tld: string) =>
    setSelectedTlds(prev => prev.includes(tld) ? prev.filter(t => t !== tld) : [...prev, tld]);

  const handleSearch = () => {
    const trimmedInput = input.trim();
    if (!trimmedInput) {
      setError(`Please enter a ${searchType === 'keyword' ? 'keyword' : 'domain name'}`);
      return;
    }
    setError(null);

    if (searchType === 'domain') {
      if (!validateDomain(trimmedInput)) {
        setError('Please enter a valid domain name (e.g., example.com)');
        return;
      }
      onSubmit ? onSubmit(trimmedInput, 'domain') : navigate(`/domain/${encodeURIComponent(trimmedInput)}`);
    } else {
      if (!validateKeyword(trimmedInput)) {
        setError('Please enter a valid keyword (letters, numbers, and hyphens only, no dots)');
        return;
      }
      if (selectedTlds.length === 0) {
        setError('Please select at least one TLD variant to scan');
        return;
      }
      if (onSubmit) {
        onSubmit(trimmedInput, 'keyword', selectedTlds);
      } else {
        const params = new URLSearchParams();
        params.set('q', trimmedInput);
        if (selectedTlds.length < POPULAR_TLDS.length) params.set('tlds', selectedTlds.join(','));
        navigate({ pathname: '/search', search: `?${params.toString()}` });
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  return (
    <div className="w-full max-w-2xl mx-auto space-y-4 fade-in">

      {/* Search type tabs */}
      <div className="flex items-center bg-gray-100 rounded-md p-1">
        <button
          onClick={() => handleSearchTypeChange('keyword')}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded text-sm font-semibold transition-all duration-200 ${
            searchType === 'keyword'
              ? 'bg-white text-blue-500'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <Globe className="h-4 w-4" />
          Keyword Search
        </button>
        <button
          onClick={() => handleSearchTypeChange('domain')}
          className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded text-sm font-semibold transition-all duration-200 ${
            searchType === 'domain'
              ? 'bg-white text-blue-500'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <Search className="h-4 w-4" />
          Single Domain
        </button>
      </div>

      {/* Input row */}
      <div className="flex gap-3">
        <div className="relative flex-1">
          <input
            ref={inputRef}
            type="text"
            placeholder={
              searchType === 'keyword'
                ? 'Enter a keyword (e.g., startup, mybrand)'
                : 'Enter a domain name (e.g., example.com)'
            }
            value={input}
            onChange={(e) => { setInput(e.target.value); setError(null); }}
            onKeyDown={handleKeyDown}
            className={`w-full h-14 px-4 text-base bg-gray-100 rounded-md text-gray-900 placeholder-gray-400 outline-none transition-all duration-200 ${
              error ? 'ring-2 ring-red-400 bg-red-50' : 'focus:bg-white focus:ring-2 focus:ring-blue-500'
            }`}
          />
          {input && (
            <button
              onClick={() => { setInput(''); setError(null); }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <button
          onClick={handleSearch}
          disabled={!input.trim() || (searchType === 'keyword' && selectedTlds.length === 0)}
          className="h-14 px-6 bg-blue-500 text-white font-semibold rounded-md transition-all duration-200 hover:bg-blue-600 hover:scale-105 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100 flex items-center gap-2 whitespace-nowrap"
        >
          <Search className="h-4 w-4" />
          {searchType === 'keyword' ? 'Scan Variants' : 'Scan Domain'}
        </button>
      </div>

      {/* TLD picker (keyword mode) */}
      {searchType === 'keyword' && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setShowTldOptions(!showTldOptions)}
              className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-blue-500 transition-colors"
            >
              <span>TLD Variants</span>
              <span className="bg-blue-100 text-blue-600 text-xs font-semibold px-2 py-0.5 rounded-md">
                {selectedTlds.length} selected
              </span>
            </button>
            {showTldOptions && (
              <div className="flex gap-2">
                <button
                  onClick={() => setSelectedTlds(POPULAR_TLDS)}
                  className="text-xs px-2 py-1 text-blue-500 hover:bg-blue-50 rounded-md transition-colors font-medium"
                >
                  All
                </button>
                <button
                  onClick={() => setSelectedTlds([])}
                  className="text-xs px-2 py-1 text-gray-500 hover:bg-gray-100 rounded-md transition-colors font-medium"
                >
                  None
                </button>
              </div>
            )}
          </div>

          {showTldOptions && (
            <div className="bg-white border-2 border-gray-200 rounded-md p-3 max-h-52 overflow-y-auto">
              <div className="grid grid-cols-5 sm:grid-cols-7 md:grid-cols-9 gap-1.5">
                {POPULAR_TLDS.map((tld) => {
                  const active = selectedTlds.includes(tld);
                  return (
                    <button
                      key={tld}
                      onClick={() => toggleTld(tld)}
                      className={`relative flex items-center justify-center px-2 py-1.5 rounded text-xs font-medium transition-all duration-200 ${
                        active
                          ? 'bg-blue-500 text-white hover:bg-blue-600'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      .{tld}
                      {active && <Check className="absolute -top-1 -right-1 h-3 w-3 text-white bg-blue-500 rounded-full" />}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border-2 border-red-200 rounded-md">
          <X className="h-4 w-4 text-red-500 flex-shrink-0" />
          <p className="text-red-600 text-sm font-medium">{error}</p>
        </div>
      )}

      {/* Helper text */}
      <p className="text-center text-xs text-gray-400">
        {searchType === 'keyword'
          ? `Scanning across ${selectedTlds.length} TLD${selectedTlds.length !== 1 ? 's' : ''}`
          : 'WHOIS data, pricing, and DNS details for a specific domain'}
      </p>
    </div>
  );
};

export default EnhancedSearch;
